import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { taskAPI } from '../../services/api';
import { useToast } from '../Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/permissions';
import TaskForm from './TaskForm';
import TaskCard from './TaskCard';
import ConfirmationModal from '../ConfirmationModal';
import { formatDateToIST } from '../../utils/dateUtils';

const TaskManager = () => {
  // Define status columns with useMemo to prevent recreation on every render
  const statusColumns = useMemo(() => [
    { key: 'todo', title: 'To Do', color: 'bg-gray-800/80' },
    { key: 'in_progress', title: 'In Progress', color: 'bg-blue-900/20' },
    { key: 'review', title: 'Review', color: 'bg-yellow-900/20' },
    { key: 'done', title: 'Done', color: 'bg-green-900/20' }
  ], []);

  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });
  
  const { showToast } = useToast();
  const { checkPermission } = usePermissions();
  const queryClient = useQueryClient();

  // Fetch Kanban data
  const { data: kanbanData, isLoading: kanbanLoading } = useQuery({
    queryKey: ['tasks-kanban'],
    queryFn: async () => {
      const response = await taskAPI.getKanban();
      return response.data;
    }
  });

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: (data) => taskAPI.create(data),
    onSuccess: () => {
      showToast('Task created successfully', 'success');
      setShowTaskForm(false);
      queryClient.invalidateQueries(['tasks-kanban']);
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to create task', 'error');
    }
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => taskAPI.update(id, data),
    onSuccess: () => {
      showToast('Task updated successfully', 'success');
      setEditingTask(null);
      setShowTaskForm(false);
      queryClient.invalidateQueries(['tasks-kanban']);
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to update task', 'error');
    }
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (id) => taskAPI.delete(id),
    onSuccess: () => {
      showToast('Task deleted successfully', 'success');
      queryClient.invalidateQueries(['tasks-kanban']);
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to delete task', 'error');
    }
  });

  // Update task status mutation (for drag and drop)
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => taskAPI.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks-kanban']);
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to update task status', 'error');
    }
  });

  // Debug logging
  useEffect(() => {
    console.log('🔍 Kanban Data:', kanbanData);
    console.log('🔍 Status Columns:', statusColumns);
    
    if (kanbanData) {
      console.log('🔍 Todo tasks:', kanbanData.todo);
      console.log('🔍 In Progress tasks:', kanbanData.in_progress);
      console.log('🔍 Review tasks:', kanbanData.review);
      console.log('🔍 Done tasks:', kanbanData.done);
    }
  }, [kanbanData, statusColumns]);

  const handleCreateTask = (taskData) => {
    createTaskMutation.mutate(taskData);
  };

  const handleUpdateTask = (id, taskData) => {
    updateTaskMutation.mutate({ id, data: taskData });
  };

  const handleDeleteTask = (id) => {
    setTaskToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (taskToDelete) {
      deleteTaskMutation.mutate(taskToDelete);
      setTaskToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  const handleStatusUpdate = (taskId, newStatus) => {
    updateStatusMutation.mutate({ id: taskId, status: newStatus });
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowTaskForm(true);
  };

  const handleCloseForm = () => {
    setShowTaskForm(false);
    setEditingTask(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-gray-700 text-gray-300 border border-gray-600';
      case 'medium': return 'bg-blue-900/30 text-blue-300 border border-blue-700';
      case 'high': return 'bg-orange-900/30 text-orange-300 border border-orange-700';
      case 'urgent': return 'bg-red-900/30 text-red-300 border border-red-700';
      default: return 'bg-gray-700 text-gray-300 border border-gray-600';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'administrative': return 'bg-purple-900/30 text-purple-300 border border-purple-700';
      case 'planning': return 'bg-indigo-900/30 text-indigo-300 border border-indigo-700';
      case 'meeting': return 'bg-pink-900/30 text-pink-300 border border-pink-700';
      case 'documentation': return 'bg-teal-900/30 text-teal-300 border border-teal-700';
      case 'training': return 'bg-cyan-900/30 text-cyan-300 border border-cyan-700';
      case 'maintenance': return 'bg-amber-900/30 text-amber-300 border border-amber-700';
      default: return 'bg-gray-700 text-gray-300 border border-gray-600';
    }
  };

  if (!checkPermission(Permission.READ_TASK)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">You don't have permission to view tasks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Tasks</h1>
          <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">Organize and track your tasks with our Kanban board</p>
        </div>
        <div className="flex items-center space-x-3">
          {checkPermission(Permission.CREATE_TASK) && (
            <button
              onClick={() => setShowTaskForm(true)}
              className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-800 transition-all duration-300"
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">New Task</span>
              <span className="sm:hidden">New</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
        {/* Search Input with Clear Button */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="block w-full pl-10 pr-12 py-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
          />
          {filters.search && (
            <button
              onClick={() => setFilters({ search: '' })}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-300 rounded-full hover:bg-gray-600 transition-colors duration-200"
              title="Clear search"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Kanban Board - HTML5 Drag and Drop */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {statusColumns.map((column) => (
            <div 
              key={column.key} 
              id={column.key}
              data-status={column.key}
              className={`${column.color} backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 border border-gray-700/50 min-h-[400px] transition-all duration-200`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {column.title}
                </h3>
                <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm font-medium border border-gray-600">
                  {kanbanData?.[column.key]?.length || 0}
                </span>
              </div>
              
              {/* Drop Zone */}
              <div 
                className="min-h-[300px] p-2 rounded-lg transition-colors duration-200"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('bg-blue-500/10');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('bg-blue-500/10');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('bg-blue-500/10');
                  
                  const taskId = e.dataTransfer.getData('text/plain');
                  if (taskId && taskId !== column.key) {
                    console.log('🎯 Drop detected in column:', { taskId, newStatus: column.key });
                    handleStatusUpdate(taskId, column.key);
                  }
                }}
              >
                <div className="space-y-3">
                  {kanbanLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : (
                    <>
                      {kanbanData?.[column.key]?.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onEdit={handleEditTask}
                          onDelete={handleDeleteTask}
                          onStatusUpdate={handleStatusUpdate}
                          canEdit={checkPermission(Permission.UPDATE_TASK)}
                          canDelete={checkPermission(Permission.DELETE_TASK)}
                          getPriorityColor={getPriorityColor}
                          getCategoryColor={getCategoryColor}
                          formatDateToIST={formatDateToIST}
                          isDraggable={true}
                          status={column.key}
                        />
                      ))}
                      {(!kanbanData?.[column.key] || kanbanData[column.key].length === 0) && (
                        <div className="text-center py-8 text-gray-400 text-sm">
                          No tasks
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Task Form Modal */}
      {showTaskForm && (
        <TaskForm
          task={editingTask}
          onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
          onClose={handleCloseForm}
          isLoading={createTaskMutation.isLoading || updateTaskMutation.isLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        confirmButtonVariant="danger"
      />
    </div>
  );
};

export default TaskManager; 